import React, { useState } from 'react';
import { Button, Checkbox, Modal } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import dayjs from 'utils/dayjs';
import KlarityLogo from 'assets/logo/klarity.png';
import { getHelloKlarityHost } from 'utils/common';
import IconMarketing from './imgs/iconMarketing.svg';
import IconBilling from './imgs/iconBilling.svg';
import IconInfo from './imgs/iconInfo.svg';
import s from './s.module.less';

interface Props {
    open: boolean;
    onAgree: () => void | Promise<void>;
}

const SUMMARY_ITEMS = [
    {
        icon: IconMarketing,
        title: 'No changes to your marketing services',
        desc: 'Your existing marketing services and pricing remain unchanged.',
    },
    {
        icon: IconBilling,
        title: 'New terms for credentialing and billing',
        desc: 'The updated agreement adds terms covering insurance credentialing and claims billing.',
    },
    {
        icon: IconInfo,
        title: 'Agreement does not enroll you',
        desc: 'Accepting these terms does not enroll you in credentialing. Enrollment is completed separately through credentialing onboarding.',
    },
];

const AGREEMENT_URL = `${getHelloKlarityHost()}/provider-terms-conditions`;

const ProviderTermsUpdateModal = ({ open, onAgree }: Props) => {
    const [checked, setChecked] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleCheck = (e: CheckboxChangeEvent) => {
        setChecked(e.target.checked);
    };

    const handleAgree = async () => {
        if (!checked || loading) return;
        setLoading(true);
        try {
            await onAgree();
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            closable={false}
            maskClosable={false}
            keyboard={false}
            footer={null}
            width={696}
            zIndex={10002}
            className={s.modal}
        >
            <div className={s.wrap}>
                <div className={s.header}>
                    <img src={KlarityLogo} alt="Klarity" className={s.logo} />
                    <div className={s.headerText}>
                        <h2 className={s.title}>Klarity Health has updated the provider agreement</h2>
                        <p className={s.subtitle}>A summary of the key changes is below.</p>
                    </div>
                </div>

                <div className={s.summaryList}>
                    {SUMMARY_ITEMS.map((item) => (
                        <div key={item.title} className={s.summaryItem}>
                            <div className={s.iconWrap}>
                                <img src={item.icon} alt="" className={s.icon} />
                            </div>
                            <div className={s.summaryContent}>
                                <div className={s.summaryTitle}>{item.title}</div>
                                <div className={s.summaryDesc}>{item.desc}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className={s.checkboxWrap}>
                    <Checkbox checked={checked} onChange={handleCheck}>
                        By checking this box, I agree to the{' '}
                        <a href={AGREEMENT_URL} target="_blank" rel="noopener noreferrer">
                            Klarity Health Provider Agreement
                        </a>{' '}
                        on {dayjs().format('MM/DD/YYYY')}.
                    </Checkbox>
                </div>

                <Button
                    type="primary"
                    block
                    disabled={!checked}
                    loading={loading}
                    onClick={handleAgree}
                    className={s.agreeBtn}
                >
                    Agree
                </Button>
            </div>
        </Modal>
    );
};

export default ProviderTermsUpdateModal;
